import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import {
  FactCheck as InspectionIcon,
  Warning as WarningIcon,
  Cancel as ErrorIcon,
  CheckCircle as SuccessIcon,
  Add as AddIcon,
  TrendingUp as TrendingIcon
} from '@mui/icons-material';
import { makeApiCall } from '../../config/api';
import { format, startOfDay, endOfDay } from 'date-fns';

/**
 * Vehicle Inspections Dashboard Widget
 * Shows today's inspection status and recent defects
 */
const InspectionsDashboardWidget = ({ onNavigateToInspections, onNavigateToNewInspection }) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    today_total: 0,
    today_major: 0,
    today_minor: 0,
    today_pass: 0,
    week_total: 0
  });
  const [recentInspections, setRecentInspections] = useState([]);

  useEffect(() => {
    fetchInspectionStats();
    fetchRecentInspections();
  }, []);

  const fetchInspectionStats = async () => {
    try {
      const today = new Date();
      const response = await makeApiCall(
        `api/inspections/stats/summary?from_date=${startOfDay(today).toISOString()}&to_date=${endOfDay(today).toISOString()}`
      );

      if (response.ok) {
        const data = await response.json();
        setStats({
          today_total: parseInt(data.total_inspections) || 0,
          today_major: parseInt(data.major_defects) || 0,
          today_minor: parseInt(data.minor_defects) || 0,
          today_pass: parseInt(data.no_defects) || 0
        });
      }
    } catch (error) {
      console.error('Error fetching inspection stats:', error);
    }
  };

  const fetchRecentInspections = async () => {
    try {
      const response = await makeApiCall('api/inspections?limit=5');

      if (response.ok) {
        const data = await response.json();
        setRecentInspections(data.inspections || []);
      }
    } catch (error) {
      console.error('Error fetching recent inspections:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDefectIcon = (inspection) => {
    if (inspection.has_major_defect) return <ErrorIcon color="error" fontSize="small" />;
    if (inspection.has_minor_defect) return <WarningIcon color="warning" fontSize="small" />;
    return <SuccessIcon color="success" fontSize="small" />;
  };

  if (loading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  const outOfServiceCount = stats.today_major;
  const needsAttentionCount = stats.today_minor;

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InspectionIcon color="primary" />
            <Typography variant="h6">
              Vehicle Inspections
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            onClick={onNavigateToInspections}
          >
            View All
          </Button>
        </Box>

        {/* Critical Alerts */}
        {outOfServiceCount > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              ⚠️ {outOfServiceCount} Vehicle{outOfServiceCount > 1 ? 's' : ''} Out of Service
            </Typography>
            <Typography variant="caption">
              Major defects found - vehicles must not be operated
            </Typography>
          </Alert>
        )}

        {needsAttentionCount > 0 && outOfServiceCount === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              {needsAttentionCount} Vehicle{needsAttentionCount > 1 ? 's' : ''} Need{needsAttentionCount === 1 ? 's' : ''} Attention
            </Typography>
            <Typography variant="caption">
              Minor defects require maintenance
            </Typography>
          </Alert>
        )}

        {/* Today's Stats */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={6}>
            <Box sx={{ 
              bgcolor: 'primary.lighter', 
              p: 2, 
              borderRadius: 1, 
              textAlign: 'center',
              border: 1,
              borderColor: 'primary.main'
            }}>
              <Typography variant="h4" color="primary" fontWeight="bold">
                {stats.today_total}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Today's Inspections
              </Typography>
            </Box>
          </Grid>
          <Grid size={6}>
            <Box sx={{ 
              bgcolor: 'success.lighter', 
              p: 2, 
              borderRadius: 1, 
              textAlign: 'center',
              border: 1,
              borderColor: 'success.main'
            }}>
              <Typography variant="h4" color="success.main" fontWeight="bold">
                {stats.today_pass}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Passed
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Status Breakdown */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            Status Breakdown
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              icon={<ErrorIcon />}
              label={`${stats.today_major} Major`}
              color="error"
              size="small"
              variant={stats.today_major > 0 ? 'filled' : 'outlined'}
            />
            <Chip
              icon={<WarningIcon />}
              label={`${stats.today_minor} Minor`}
              color="warning"
              size="small"
              variant={stats.today_minor > 0 ? 'filled' : 'outlined'}
            />
            <Chip
              icon={<SuccessIcon />}
              label={`${stats.today_pass} Pass`}
              color="success"
              size="small"
              variant={stats.today_pass > 0 ? 'filled' : 'outlined'}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Recent Inspections */}
        <Box>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            Recent Inspections
          </Typography>
          {recentInspections.length === 0 ? (
            <Typography variant="body2" color="textSecondary" sx={{ py: 2, textAlign: 'center' }}>
              No inspections yet today
            </Typography>
          ) : (
            <List dense sx={{ py: 0 }}>
              {recentInspections.slice(0, 3).map((inspection) => (
                <ListItem 
                  key={inspection.id}
                  sx={{ 
                    px: 0,
                    py: 0.5,
                    '&:hover': { bgcolor: 'action.hover', cursor: 'pointer', borderRadius: 1 }
                  }}
                  onClick={() => onNavigateToInspections && onNavigateToInspections()}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                    {getDefectIcon(inspection)}
                    <ListItemText
                      primary={
                        <Typography variant="body2" noWrap>
                          {inspection.vehicle_name}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="textSecondary">
                          {format(new Date(inspection.created_at), 'h:mm a')}
                        </Typography>
                      }
                    />
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        {/* Quick Action */}
        <Box sx={{ mt: 2 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onNavigateToNewInspection}
            size="small"
          >
            New Inspection
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default InspectionsDashboardWidget;
